import { Routes, Route, useLocation } from "react-router-dom";
import Dashboard from "./pages/Dashboard";
import Layout from "./layout/Layout";
import RegisterPage from "./pages/register";
import LoginPage from "./pages/login";
import VerifyInvitationPage from "./pages/verify-invitation";
import ProtectedRoute from "./components/ProtectedRoute";
import { useEffect } from "react";
import { getKeycloakUser } from "./utils/keyclock";
import WrongPortalPage from "./pages/wrongPortal";
import NotFound from "../../../packages/ui/src/pages/notFound";
import Catalog from "./pages/Catalog";
import Users from "./pages/Users";
import UserDetails from "./pages/UserDetails";
import Branches from "./pages/Branches";
import BranchDetails from "./pages/BranchDetails";
import PurchaseRequest from "./pages/PurchaseRequest";
import PurchaseOrders from "./pages/PurchaseOrders";
import PurchaseOrderCreation from "./pages/purchaseOrderCreation";
import PurchaseOrderDetailsView from "./pages/PurchaseOrderDetailsView";
import {
  HUB_PUBLIC_ROUTES,
  HUB_PROTECTED_ROUTES,
  isPublicRoute,
  isNotFoundRoute,
} from "./routes";
import { permissions } from "./permissions";
import { useAuth } from "react-oidc-context";
import { Provider, useDispatch } from "react-redux";
import { store } from "./store";
import { setGlobalState, setLoading, logout } from "./store/auth";
import { toast } from "sonner";
import SignOut from "./pages/SignOut";

function App() {
  const location = useLocation();
  const keycloakUser = getKeycloakUser();
  const auth = useAuth();
  const dispatch = useDispatch();
  // Monitor online/offline status
  useEffect(() => {
    const handleOnlineStatusChange = () => {
      if (navigator.onLine) {
        toast.success("You are back online!", {
          duration: 3000,
        });
      } else {
        toast.error("You are offline. Please check your internet connection.", {
          duration: 5000,
        });
      }
    };

    // Listen for online/offline events
    window.addEventListener("online", handleOnlineStatusChange);
    window.addEventListener("offline", handleOnlineStatusChange);

    // Cleanup event listeners on unmount
    return () => {
      window.removeEventListener("online", handleOnlineStatusChange);
      window.removeEventListener("offline", handleOnlineStatusChange);
    };
  }, []);

  useEffect(() => {
    // Check for pending auth data from OIDC callback
    const pendingAuthData = sessionStorage.getItem("pendingAuthData");

    if (pendingAuthData) {
      try {
        const authData = JSON.parse(pendingAuthData);
        // Extract minimal user data and dispatch as trial data
        if (authData.userInfo) {
          const userInfo = authData.userInfo;

          dispatch(
            setGlobalState({
              first_name: userInfo.first_name || null,
              last_name: userInfo.last_name || null,
              full_name: userInfo.full_name || null,
              email: userInfo.email || null,
              mobile: userInfo.mobile || null,
              subject_id: userInfo.subject_id || null,
              creation_date: userInfo.creation_date || null,
              last_accessed_branch_id: userInfo.last_accessed_branch_id || null,
              user_groups: userInfo.user_groups || [],
              user_permissions: userInfo.user_permissions || [],
              last_login: userInfo.last_login || "",
              user_branches: userInfo.user_branches
                ? userInfo.user_branches
                    .map((branch: { id: number; name: string }) => branch.id)
                    .join(",")
                : "",
              // Token data from auth payload
              access_token: authData.access_token || "",
              id_token: authData.id_token || "",
              refresh_token: authData.refresh_token || "",
              expires_at: authData.expires_at || 0,
              tenant_id: authData.tenant_id || 0,
            })
          );
          sessionStorage.removeItem("pendingAuthData");
          dispatch(setLoading(false));
          // console.log("❌ No userInfo.user_info found in authData");
        }
      } catch (error) {
        // console.error("❌ Error parsing pending auth data:", error);
      }
    }
  }, [dispatch]);
  useEffect(() => {
    // Listen for logout events from API client
    const handleAuthLogout = (event: Event) => {
      const customEvent = event as CustomEvent;
      console.log("401 detected, logging out...", customEvent.detail);

      localStorage.clear();
      sessionStorage.clear();
      // Perform Keycloak logout
      auth.signoutRedirect();

      // auth?.signoutSilent();
      // Clear Redux state
      auth?.removeUser();
      store.dispatch(logout());
    };

    // Add event listener - this automatically filters for "auth:logout" events
    window.addEventListener("auth:logout", handleAuthLogout);

    // Cleanup on unmount
    return () => {
      window.removeEventListener("auth:logout", handleAuthLogout);
    };
  }, [auth]);
  useEffect(() => {
    const authChannel = new BroadcastChannel("auth-sync");

    // Listen for auth events from other tabs
    authChannel.onmessage = (event) => {
      const { type, data } = event.data;

      switch (type) {
        case "LOGOUT":
          dispatch(logout());
          auth.removeUser();
          // Don't redirect here, let the component handle it
          break;
        case "LOGIN":
          dispatch(setGlobalState(data));
          break;
        case "TOKEN_REFRESH":
          // Update tokens across tabs
          localStorage.setItem("token", data.access_token);
          break;
      }
    };

    // Cleanup
    return () => {
      authChannel.close();
    };
  }, [dispatch, auth]);
  useEffect(() => {
    if (keycloakUser?.access_token)
      localStorage.setItem("token", keycloakUser?.access_token || "");
  }, [keycloakUser?.access_token]);
  // If current path is a public route - render without layout
  if (isPublicRoute(location.pathname) || isNotFoundRoute(location.pathname)) {
    return (
      <Routes>
        <Route path={HUB_PUBLIC_ROUTES.REGISTER} element={<RegisterPage />} />
        <Route path={HUB_PUBLIC_ROUTES.LOGIN} element={<LoginPage />} />
        <Route
          path={HUB_PUBLIC_ROUTES.VERIFY_INVITATION}
          element={<VerifyInvitationPage />}
        />
        <Route
          path={HUB_PUBLIC_ROUTES.WRONG_PORTAL}
          element={<WrongPortalPage />}
        />
        <Route path={HUB_PUBLIC_ROUTES.SIGN_OUT} element={<SignOut />} />
        <Route path={HUB_PUBLIC_ROUTES.NOT_FOUND} element={<NotFound />} />
      </Routes>
    );
  }

  // Protected routes that need authentication - render with layout
  return (
    <Provider store={store}>
      <Routes>
        {/* Routes with layout */}
        <Route path={HUB_PROTECTED_ROUTES.HOME} element={<Layout />}>
          <Route
            path={HUB_PROTECTED_ROUTES.PURCHASE_ORDER_VIEW}
            element={
              <ProtectedRoute>
                <PurchaseOrderDetailsView />
              </ProtectedRoute>
            }
          />
          <Route
            path={HUB_PROTECTED_ROUTES.PURCHASE_ORDER_CREATION}
            element={
              <ProtectedRoute
                requiredPermissions={[permissions.EDIT_DRAFT_PURCHASE_ORDER]}
              >
                <PurchaseOrderCreation />
              </ProtectedRoute>
            }
          />
          <Route
            path={HUB_PROTECTED_ROUTES.HOME}
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path={HUB_PROTECTED_ROUTES.ORDERS}
            element={
              <ProtectedRoute
                requiredPermissions={[permissions.VIEW_PURCHASE_ORDER]}
              >
                <PurchaseOrders />
              </ProtectedRoute>
            }
          />
          <Route
            path={HUB_PROTECTED_ROUTES.PROFILE}
            element={
              <ProtectedRoute>
                <div>Profile Page</div>
              </ProtectedRoute>
            }
          />
          <Route
            path={HUB_PROTECTED_ROUTES.BRANCHES}
            element={
              <ProtectedRoute requiredPermissions={[permissions.VIEW_BRANCHES]}>
                <Branches />
              </ProtectedRoute>
            }
          />
          <Route
            path={HUB_PROTECTED_ROUTES.USERS}
            element={
              <ProtectedRoute
                requiredPermissions={[permissions.VIEW_BRANCH_USERS]}
              >
                <Users />
              </ProtectedRoute>
            }
          />
          <Route
            path={HUB_PROTECTED_ROUTES.USER_DETAILS}
            element={
              <ProtectedRoute
                requiredPermissions={[permissions.VIEW_BRANCH_USERS]}
              >
                <UserDetails />
              </ProtectedRoute>
            }
          />
          <Route
            path={HUB_PROTECTED_ROUTES.CATALOG}
            element={
              <ProtectedRoute
                requiredPermissions={[permissions.VIEW_TENANT_PRODUCTS]}
              >
                <Catalog />
              </ProtectedRoute>
            }
          />
          <Route
            path={HUB_PROTECTED_ROUTES.PURCHASE_REQUESTS}
            element={
              <ProtectedRoute
                requiredPermissions={[permissions.VIEW_PURCHASE_REQUEST]}
              >
                <PurchaseRequest />
              </ProtectedRoute>
            }
          />
          <Route
            path={HUB_PROTECTED_ROUTES.BRANCH_DETAILS}
            element={
              <ProtectedRoute>
                <BranchDetails />
              </ProtectedRoute>
            }
          />
        </Route>
      </Routes>
    </Provider>
  );
}

export default App;
